import * as React from 'react';
import styled from 'styled-components';
import RefreshIcon from '@material-ui/icons/Refresh';
import EditIcon from '@material-ui/icons/Edit';

import tableColumnsFormatter from '../utils/tableColumnsFormatter';
import DashboardLayout from '../layouts/DashboardLayout';
import Table from '../organisms/Table';
import ActionsPanel from '../organisms/ActionsPanel';
import useTablePage from '../hooks/useTablePage';
import useCanSendInspectorMotd from '../hooks/useCanSendInspectorMotd';
import { convertTableUTCDate } from '../utils/convertTableValues';
import { ModalDefaultDraggable } from '../molecules';
import ModalInfo from '../molecules/ModalInfo';
import InspectorMotdForm from '../forms/InspectorMotdForm';
import {
  getInspectorMotdSent,
  InspectorMotdSentRow,
  reuseInspectorMotd,
  sendInspectorMotd,
  updateInspectorMotd,
} from '../api/inspectorMotd';

/**
 * The designated inspector's own Messages page.
 *
 * Same layout as the Branch Admin's Settings > Messages (search + add panel,
 * history table, Reuse action, modal compose form) — inspectors just have no
 * Settings section, so it lives at its own top-level route.
 */
const InspectorMessagesPage = () => {
  const { canSend, isChecked } = useCanSendInspectorMotd();

  const {
    search,
    setSearch,
    modalError,
    modalSuccess,
    isModalOpen,
    modalTitle,
    values,
    modalErrorTitle,
    errors,
    setErrors,
    tableManuallyUpdate,
    updateTable,
    createOrUpdate,
    handleAdd,
    handleEdit,
    handleCloseModal,
    handleCloseModalInfo,
  } = useTablePage<InspectorMotdSentRow>({
    createEndpoint: fields =>
      sendInspectorMotd({ text: fields.text }),
    // Only the text is editable once a message is out.
    updateEndpoint: fields =>
      updateInspectorMotd(fields.id, { text: fields.text }),
    modalCreateTitle: 'Send Message of the Day',
    modalUpdateTitle: 'Update Message of the Day',
  });

  const columns = column => {
    if (['id'].includes(column)) {
      return null;
    }

    if (column === 'publicationDate') {
      return 'Date';
    }

    return tableColumnsFormatter(column);
  };

  // The table fires row actions more than once per click; without this the
  // Reuse would post two copies of the same message.
  const isReuseInFlight = React.useRef(false);

  const additionalActions = () => [
    {
      title: 'Edit',
      handler: (fields: InspectorMotdSentRow) =>
        handleEdit(fields),
      Icon: EditIcon,
    },
    {
      title: 'Reuse',
      handler: (fields: InspectorMotdSentRow) => {
        if (isReuseInFlight.current) return;

        isReuseInFlight.current = true;
        reuseInspectorMotd({ id: fields.id })
          .then(() => updateTable())
          .catch(() => setErrors(null))
          .finally(() => {
            isReuseInFlight.current = false;
          });
      },
      Icon: RefreshIcon,
    },
  ];

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  if (isChecked && !canSend) {
    return (
      <DashboardLayout>
        <Page>
          <NotAllowed>
            You are not set up to send the Message of the Day.
            Ask a Super Admin to designate you in
            Settings &gt; Message Setup.
          </NotAllowed>
        </Page>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <ActionsPanel
        isActionPanelMoreVisible={false}
        onSearch={setSearch}
        onAdd={handleAdd}
      />

      {canSend && (
        <Page>
          <Table
            customSearch
            searchQuery={search}
            columns={columns}
            rows={getInspectorMotdSent}
            formatValue={convertTableUTCDate}
            manuallyUpdate={tableManuallyUpdate}
            additionalActions={additionalActions}
          />
        </Page>
      )}

      {isModalOpen && (
        <ModalDefaultDraggable
          onClose={handleCloseForm}
          title={modalTitle}
        >
          <InspectorMotdForm
            initialValues={values}
            initialErrors={errors}
            modalError={modalErrorTitle.length > 0}
            onSubmit={createOrUpdate}
          />
        </ModalDefaultDraggable>
      )}

      {(modalSuccess || modalError) && (
        <ModalInfo
          timeToClose={5000}
          isError={modalError}
          onClose={handleCloseModalInfo}
          errorMsg={modalErrorTitle}
        />
      )}
    </DashboardLayout>
  );
};

const Page = styled.div`
  padding: 20px 16px 0;
  box-sizing: border-box;
  width: 100%;

  @media only screen and (max-width: 840px) {
    padding: 12px 12px 0;
  }
`;

const NotAllowed = styled.div`
  text-align: center;
  padding: 48px 16px;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

export default InspectorMessagesPage;
